import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import api from "../api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Send } from "lucide-react";

export default function TaskCommentsPanel({ task, onClose, onChanged, embedded = false }) {
  const { t, user } = useApp();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/tasks/${task.id}/comments`).then((r) => setComments(r.data)).catch(() => {});
  useEffect(() => { load(); }, [task.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tasks/${task.id}/comments`, { text });
      setText("");
      await load();
      onChanged();
    } catch { toast.error(t("msg.error")); } finally { setBusy(false); }
  };

  const body = (
    <>
        <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1" data-testid="task-comments-list">
          {comments.length === 0 && <div className="text-sm text-muted-foreground py-4 text-center">{t("task.noComments")}</div>}
          {comments.map((c) => {
            const mine = c.author_id === user?.user_id;
            return (
              <div key={c.id} className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`} data-testid={`task-comment-${c.id}`}>
                <Avatar className="h-7 w-7 shrink-0"><AvatarFallback className="text-[10px]">{c.author_name.slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                <div className={`rounded-lg px-3 py-2 text-sm max-w-[80%] ${mine ? "bg-primary/15" : "bg-muted"}`}>
                  <div className="text-[10px] text-muted-foreground mb-0.5">{c.author_name} · {c.created_at.slice(0, 16).replace("T", " ")}</div>
                  <div className="whitespace-pre-wrap break-words">{c.text}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-2 items-end pt-2 border-t border-border">
          <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("task.addComment")}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }} data-testid="task-comment-input" />
          <Button onClick={send} disabled={busy || !text.trim()} data-testid="task-comment-send"><Send className="h-4 w-4" /></Button>
        </div>
    </>
  );
  if (embedded) return <div className="space-y-3" data-testid="task-comments-dialog">{body}</div>;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="task-comments-dialog">
        <DialogHeader><DialogTitle className="truncate">{t("task.comments")} — {task.title}</DialogTitle></DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
